import { Unit } from './api'

import { StatusDisplayCellColor } from '../../components/displayContent/interfaces'

import { UnitType } from './unittype'

/**
 * Product database interface.
 *
 * This corresponds to a single table in the database.
 */
export interface IProductDatabase {
  /**
   * Unique identifier of the product database.
   */
  id: string
  /**
   * ID of the user who created the product database.
   */
  user_id: string
  /**
   * ID of the organization that the product database belongs to.
   */
  organization_id: string
  /**
   * Confidence score threshold for the product database.
   */
  confidence_score_threshold: number
  /**
   * Whether the product database is the master PDB
   */
  is_master: boolean

  /**
   * Level group keys for product variant hierarchy.
   * Example: {"1": ["Supplier-Style-Color"], "2": ["Supplier-Style"]}
   * NOTE: Level group keys should NEVER be used anywhere in the frontend except for initial setup purposes.
   */
  level_group_keys?: Record<string, string[]>

  created_at: string
  updated_at: string
  deleted_at?: string
  deleted: boolean
}

/**
 * Product database enum.
 *
 * Type of product database.
 */
export enum ProductDatabaseType {
  /**
   * Master database.
   *
   * This is the type of product database that is isolated and is not part
   * of any project in the projects page.
   */
  master = 'master',

  /**
   * Project database.
   *
   * This is the type of product database that is part of the
   * projects page.
   */
  project = 'project'
}

/**
 * Product attribute type.
 *
 * Data type of a product attribute.
 */
export enum ProductAttributeType {
  /**
   * Short text field.
   * This is used for fields that are expected to be short strings.
   * e.g. product name, brand name, etc.
   */
  short_text = 'short_text',

  /**
   * Long text field.
   * This is used for fields that are expected to be long strings.
   * e.g. product description, product bullet points, etc.
   */
  long_text = 'long_text',

  /**
   * Number field.
   * e.g. product price, product weight, etc.
   */
  number = 'number',

  /**
   * Datetime field.
   * e.g. product release date, product expiry date, etc.
   */
  datetime = 'datetime',

  /**
   * URL field.
   * e.g. product PDP URL, product video URL, etc.
   */
  url = 'url',

  /**
   * File references field.
   * e.g. product manual PDFs.
   */
  file_references = 'file_references',

  /**
   * Media URLs field.
   * e.g. product image URLs.
   */
  media_urls = 'media_urls',

  /**
   * Single tag field.
   * e.g. product category, product sub-category, etc.
   */
  single_tag = 'single_tag',

  /**
   * Multi tags field.
   * e.g. product tags, SEO keywords, etc.
   */
  multi_tags = 'multi_tags',

  /**
   * Multi URLs field.
   * e.g. product image URLs, product video URLs, source URLs, etc.
   *
   * One important use case for this is to store source URLs for an
   * enrichment task.
   */
  multi_urls = 'multi_urls',

  /**
   * Status tracker field.
   * e.g. approved, pending, needs enrichment, etc.
   *
   * Shows different option types depending on user-defined conditional logic.
   */
  status_tracker = 'status_tracker',

  /**
   * Taxonomy category field.
   *
   * Behaves *mostly* the same way as a single tag field.
   *
   * The only difference is that the value is a taxonomy category ID,
   * and not a string.
   */
  taxonomy_category = 'taxonomy_category',

  /**
   * Digital asset references field.
   *
   * This field is used to link a product item to one or more digital assets.
   */
  asset_references = 'asset_references'
}

/**
 * Product attribute field kind.
 *
 * This determines the association of a column to different entities.
 * For example, it could be linked to a particular taxonomy category.
 */
export enum ProductAttributeFieldKind {
  /**
   * Taxonomy field.
   *
   * This field is linked to one or more taxonomy categories.
   */
  taxonomy = 'taxonomy',

  /**
   * General field.
   *
   * Any arbitrary field that is created by the user.
   */
  general = 'general',

  /**
   * Status tracker field.
   *
   * This field is used to track the status of a product item using
   * conditional logic.
   */
  status_tracker = 'status_tracker',

  /**
   * Internal field.
   *
   * This field is used for columns that are not visible to the user.
   */
  internal = 'internal'
}

/**
 * Type alias for HypoField values that can be used as field types in the frontend.
 * This represents the subset of HypoField values that users can select when creating new fields.
 */
export interface AddNewFieldModalFieldTypeConfig {
  SEOKeywordsField: {
    dataType: ProductAttributeType.multi_tags
    displayName: 'SEO Keywords'
    hypoFieldName: HypoField.seo_keywords
  }
  ManuallyCreatedField: {
    dataType: ProductAttributeType
    displayName: 'Custom field'
    hypoFieldName: undefined
  }
}

export type FieldTypeKey = keyof AddNewFieldModalFieldTypeConfig

/**
 * Product attribute type used for display in a table cell.
 *
 * This is decoupled from the actual data type of the attribute.
 */
export type ProductAttributeTypeTableCell = ProductAttributeType

/**
 * Product attribute field metadata
 */
export interface ProductAttributeFieldMetadata {
  /**
   * Unit of the attribute.
   */
  unit?: Unit
  /**
   * Whether the user chose to enable the LOV for the attribute
   */
  is_lov_enabled?: boolean
  /**
   * Whether the AI can suggest values outside of the LOV for the attribute during enrichment.
   */
  is_suggested_list_of_values_allowed_during_enrichment?: boolean
  /**
   * Additional metadata for the attribute.
   */
  [key: string]: any
}

/**
 * Base interface for all attribute fields in the system.
 *
 * Attribute fields are used to define the structure and metadata of both
 * product attributes and taxonomy attributes. They share common properties,
 * but have specialized behavior based on their specific type.
 */
interface BaseProductAttributeField {
  id: string
  /**
   * ID of the product database that the attribute field belongs to.
   */
  product_database_id?: string
  /**
   * Data type of the attribute.
   */
  attribute_type: ProductAttributeType
  /**
   * Kind of the attribute. Be it a product attribute field or a taxonomy attribute.
   */
  field_kind?: ProductAttributeFieldKind
  /**
   * Level of the attribute field.
   */
  level: number

  /**
   * Hypo field name. This is only used for predefined fields
   * for internal use across different workflows.
   */
  hypo_field_name?: HypoField
  /**
   * Type of tracker, if the attribute is a status tracker field.
   */
  tracker_type?: StatusTrackerType

  /**
   * Additional metadata for the attribute.
   */
  attribute_metadata: ProductAttributeFieldMetadata

  /**
   * Foreign ID of the attribute field. This is used to link the attribute field to an external system.
   */
  foreign_id?: string

  /**
   * Export identifier of the attribute field. This is used to link the attribute field to an external system during export
   */
  foreign_export_id?: string

  /**
   * Display order of the attribute field.
   */
  display_order?: number

  /**
   * Whether the attribute is required.
   */
  is_required: boolean
  /**
   * Whether the attribute is editable.
   */
  is_editable: boolean
  /**
   * Whether the attribute is deletable.
   */
  is_deletable: boolean
  /**
   * Whether the attribute is enrichable.
   */
  is_enrichable: boolean
  /**
   * Whether the enriched attribute value is approved
   */
  is_approved?: boolean
  /**
   * Whether the attribute is visible on the table.
   */
  is_visible: boolean
  /*
   * Whether the attribute is pinned to the start of the view.
   */
  is_pinned?: boolean

  /**
   * ID of the user who created the attribute field.
   */
  user_id: string
  /**
   * ID of the organization that the attribute field belongs to.
   */
  organization_id: string
  created_at: string
  updated_at: string
  deleted: boolean
  deleted_at?: string
}

/**
 * Details of a product attribute field localized to a specific locale.
 */
export interface ProductAttributeFieldLocalizedDetails {
  /**
   * Display name of the attribute.
   */
  attribute_display_name: string
  /**
   * Definition of the attribute.
   */
  attribute_definition?: string
  /**
   * Guidelines for the attribute.
   */
  attribute_guidelines?: string
  /**
   * A unique identifier for the unit of the attribute field.
   */
  attribute_unit?: UnitType
}

/**
 * Product attribute field.
 *
 * This corresponds to a single column in the product database table.
 */
export interface ProductAttributeField
  extends BaseProductAttributeField,
    ProductAttributeFieldLocalizedDetails {}

/**
 * Product attribute field with multi-locale details.
 *
 * This is used to store the localized details of the attribute field for each locale.
 */
export interface ProductAttributeFieldWithMultiLocaleDetails
  extends BaseProductAttributeField {
  locale_id_to_details_mapping: Record<
    string,
    ProductAttributeFieldLocalizedDetails
  >
}

export type ProductAttributeValueType =
  | string
  | number
  | boolean
  | Date
  | string[]
  | null

export interface DistinctProductAttributeValuesResponse {
  distinct_values: ProductAttributeValueOption[]
}

/**
 * Interface used to represent a product attribute value option in a dropdown list.
 */
export interface ProductAttributeValueOption {
  /**
   * Value of the product attribute.
   */
  value: ProductAttributeValueType
  /**
   * String that should be displayed to the user to represent the value.
   *
   * For text-like attributes, this is the same as the value.
   * But for special cases like taxonomy categories, this is the display name of the
   * category, and the value is the ID of the category.
   */
  display_name: string
}

export interface ProductAttributeValue {
  value: ProductAttributeValueType
  product_attribute_field_allowed_value_id?: string
}

export interface UpdatedProductItemAttributesResponse {
  product_item_id: string
  updated_product_attributes: ProductAttribute[]
  deleted_product_attributes: ProductAttribute[]
}

export interface ProductAttributeValuesUpdateResponse {
  updated_product_items: UpdatedProductItemAttributesResponse[]
}

/**
 * Product attribute.
 *
 * This corresponds to a value in a product attribute field.
 */
export interface ProductAttribute {
  /**
   * ID of the product attribute field.
   */
  attribute_field_id: string
  /**
   * Type of the product attribute.
   */
  attribute_type: ProductAttributeType
  /**
   * Value of the product attribute.
   */
  attribute_value: ProductAttributeValue
  /**
   * Whether the attribute is required.
   */
  is_required: boolean
  /**
   * Timestamp when the product atttribute value was created.
   */
  created_at: string
  /**
   * Timestamp when the product attribute value was last updated.
   */
  updated_at: string
  /**
   * Additional metadata for the attribute.
   */
  attribute_metadata: Record<string, any>
  /**
   * Confidence score for the attribute.
   */
  confidence_score?: number
  /**
   * Approval status for the attribute
   */
  approved?: boolean
  /**
   * Whether the product has been deleted.
   */
  is_deleted?: boolean
  /**
   * Whether the attribute value is AI generated.
   */
  is_ai_generated?: boolean
  /**
   * Source of the attribute
   */
  ai_generation_source?: string
}

/**
 * Product item.
 *
 * This corresponds to a single row in the product database table.
 */
export interface IProductItem {
  /**
   * Product item ID.
   */
  id: string

  /**
   * Product database ID of this product item.
   */
  product_database_id: string

  /**
   * Unique ID of the product item.
   * This helps in identifying the product across different workflows in the platform.
   * For example, tracking the product item across analytics dashboard, product description catalog, etc.
   */
  unique_id: string

  /**
   * An foreign key used to sync products from other services.
   * For example, for Shopify, this is the shopify product ID. Allows us to know which Shopify product this product item corresponds to.
   */
  sync_id?: string

  /**
   * User ID of the user to whom the product item belongs.
   */
  user_id: string

  /**
   * Organization ID of the organization to which the product item belongs.
   */
  organization_id: string

  /**
   * Whether the product item is deleted.
   */
  deleted: boolean

  /**
   * Timestamp when the product item was created.
   */
  created_at: string

  /**
   * Timestamp when the product item was last updated.
   */
  updated_at: string

  /**
   * Timestamp when the product item was last exported.
   */
  exported_at?: string

  /**
   * Timestamp when the product item was last imported.
   */
  imported_at?: string

  /**
   * Timestamp when the product item was deleted.
   */
  deleted_at?: string

  /**
   * Mapping of product attribute field ID to its value.
   * This is used to store all the product attributes for a product item.
   */
  field_id_to_value_mapping?: Record<string, ProductAttribute>
}

/**
 * Hypo Field.
 *
 * These fields correspond to various inputs used across
 * the platform features in a specific way, and the meaning of these fields
 * retain ACROSS organizations.
 *
 * @todo Add individual docs for each field.
 */
export enum HypoField {
  // General
  product_title = 'product_title',
  category = 'category',
  product_tags = 'product_tags',
  brand = 'brand',
  seo_keywords = 'seo_keywords',
  // Website
  shopify_status = 'shopify_status',
  website_url = 'website_url',
  website_images = 'website_images',
  website_product_title = 'website_product_title',
  website_product_description = 'website_product_description',
  meta_title = 'meta_title',
  meta_description = 'meta_description',
  google_seo_keywords = 'google_seo_keywords',
  barcode = 'barcode',
  // Amazon
  amazon_product_id = 'amazon_product_id',
  amazon_url = 'amazon_url',
  amazon_images = 'amazon_images',
  amazon_title = 'amazon_title',
  amazon_descriptions = 'amazon_descriptions',
  amazon_bullets = 'amazon_bullets',
  amazon_seo_keywords = 'amazon_seo_keywords',
  WalmartTitle = 'walmart_title',
  WalmartDescription = 'walmart_descriptions',
  WalmartBullets = 'walmart_bullets',
  WalmartSeoKeywords = 'walmart_seo_keywords',
  WalmartUrl = 'walmart_url',
  // Internal
  created_at = 'created_at',
  updated_at = 'updated_at',
  exported_at = 'exported_at',
  imported_at = 'imported_at',
  unique_id = 'unique_id',
  // AI Actions (e.g. Enrichment)
  WebEnrichmentSources = 'web_enrichment_sources',
  PdfEnrichmentSources = 'pdf_enrichment_sources',
  ConfidenceScore = 'confidence_score',
  EnrichmentStatus = 'enrichment_status',
  // Taxonomy
  taxonomy_category = 'taxonomy_category',
  taxonomy_classpath = 'taxonomy_classpath'
}

/**
 * Default column order for the product database table.
 * @deprecated Column order is now determined by the active PDB view.
 */
export const DEFAULT_HYPO_FIELD_TO_COLUMN_ORDER: Partial<
  Record<HypoField, number>
> = {
  [HypoField.unique_id]: 0,
  [HypoField.product_title]: 1,
  [HypoField.website_images]: 2,
  [HypoField.brand]: 3,
  [HypoField.taxonomy_category]: 4,
  [HypoField.category]: 5,
  [HypoField.product_tags]: 6,
  [HypoField.website_url]: 7,
  [HypoField.shopify_status]: 8,
  [HypoField.website_product_title]: 9,
  [HypoField.website_product_description]: 10,
  [HypoField.meta_title]: 11,
  [HypoField.meta_description]: 12,
  [HypoField.google_seo_keywords]: 13,
  [HypoField.amazon_product_id]: 14,
  [HypoField.amazon_url]: 15,
  [HypoField.amazon_images]: 16,
  [HypoField.amazon_title]: 17,
  [HypoField.amazon_descriptions]: 18,
  [HypoField.amazon_bullets]: 19,
  [HypoField.amazon_seo_keywords]: 20,
  [HypoField.WalmartTitle]: 21,
  [HypoField.WalmartDescription]: 22,
  [HypoField.WalmartBullets]: 23,
  [HypoField.WalmartSeoKeywords]: 24,
  [HypoField.WalmartUrl]: 25,
  [HypoField.barcode]: 26,
  [HypoField.created_at]: 27,
  [HypoField.updated_at]: 28,
  [HypoField.exported_at]: 29,
  [HypoField.imported_at]: 30
}

export interface ProductDatabaseConfig {
  /**
   * Order of columns in the product database table.
   *
   * This is used to render the columns in the configured order in
   * the product database table.
   */
  table_column_order: Record<HypoField, number>

  column_order: Record<string, number>

  /**
   * The number of products that can be imported into the pdb.
   */
  num_product_limit: number
  /**
   * The catalogue level grouping keys to use for the product database.
   */
  catalogue_level_grouping_keys?: Record<number, string>
}

/**
 * Strategy for prioritizing specific URLs during product enrichment
 */
export enum UrlPriorityStrategy {
  /**
   * Only use the designated URL for enrichment.
   * Skip enrichment entirely if the designated URL is not available.
   */
  strict = 'strict',

  /**
   * Use designated URL as primary source if available.
   * Fall back to other sources if designated URL is not found.
   */
  preferred = 'preferred',

  /**
   * Use all available sources, while keeping to the rules in ProductWebEnrichmentLinksConfig.
   * Do not give special treatment to any specific URLs.
   */
  ignore = 'ignore'
}

export const URL_PRIORITY_STRATEGY_TO_DISPLAY_NAME_MAPPING: Record<
  UrlPriorityStrategy,
  string
> = {
  [UrlPriorityStrategy.strict]: 'Only pull from inclusion URLs.',
  [UrlPriorityStrategy.preferred]: 'Prioritize inclusion URLs.',
  [UrlPriorityStrategy.ignore]: 'Open web search.'
}

export const URL_PRIORITY_STRATEGY_TO_EXPLANATION_MAPPING: Record<
  UrlPriorityStrategy,
  string
> = {
  [UrlPriorityStrategy.strict]: 'If data is not found, skip web enrichment.',
  [UrlPriorityStrategy.preferred]:
    'Fall back to open web search if there are no products found.',
  [UrlPriorityStrategy.ignore]: 'Ignore URL settings and search the entire web.'
}

export interface ProductAttributeToEnrich {
  attribute_id: string
  attribute_name: string
  attribute_type: ProductAttributeType
  attribute_definition?: string
  attribute_guidelines?: string
  attribute_metadata: Record<string, any>
  attribute_unit?: UnitType
  attribute_level: number
}

export enum ProductEnrichmentSource {
  image_url = 'image_url',
  web = 'web',
  web_deep = 'web_deep',
  barcode = 'barcode',
  pdf = 'pdf',
  asset_references = 'asset_references'
}

/**
 * Metadata for the enrichment sources.
 */
export interface ProductEnrichmentSourceMetadata {
  sources: string[]
  source_type: ProductEnrichmentSource
}

interface ImageUrlEnrichmentSource {
  image_urls: string[]
}

interface WebEnrichmentSource {
  product_id: string
  title: string
  brand: string
}

interface BarcodeEnrichmentSource {
  barcode: string
}

type EnrichmentSourceDataType =
  | ImageUrlEnrichmentSource
  | WebEnrichmentSource
  | BarcodeEnrichmentSource

export interface ProductAttributeEnrichmentRequest {
  attributes: ProductAttributeToEnrich[]
  source_type: ProductEnrichmentSource
  source_data: EnrichmentSourceDataType
}

export interface ProductAttributeEnrichmentResult
  extends ProductAttributeToEnrich {
  attribute_value: ProductAttributeValue
  confidence_score?: number
}

export interface ProductAttributeEnrichmentResponse {
  results: ProductAttributeEnrichmentResult[]
  source_type: ProductEnrichmentSource
  enrichment_sources: string[]
}

export interface ProductItemAttributeEnrichmentResponse {
  product_item_id: string
  attribute_enrichments: ProductAttributeEnrichmentResponse
}

export interface ProductAttributeEnrichmentResultMap {
  [attributeFieldId: string]: {
    attribute_value: ProductAttributeValue
    confidence_score?: number
    approved?: boolean
  }
}

export interface ProductItemAttributeEnrichment {
  product_item_id: string
  enrichments: ProductAttributeEnrichmentResultMap
  enrichment_sources: string[]
}

export interface ProductItemAttributeEnrichmentMap {
  [productItemId: string]: ProductItemAttributeEnrichment
}

export interface ExportToProductCatalogueParams {
  mappings?: Record<string, string[]>
  catalogName: string
  wordCount?: number
  templateId: string
  export_level: number
}

// ==== Status Tracker Interface and Enums ====

/**
 * Status Tracker Type defines whether a status tracker is automatically
 * or manually changed.
 */
export enum StatusTrackerType {
  automatic = 'automatic',
  manual = 'manual'
}

/**
 * Logic Type defines the logic used to combine different attribute fields
 * together during tracking.
 */
export enum LogicType {
  any_of = 'any_of',
  all_of = 'all_of',
  /**
   * @deprecated This logic type will no longer be supported once conditional status trackers are fully supported.
   * @todo Remove this once conditional status trackers are fully supported.
   */
  none_of = 'none_of'
}

export enum AdvancedLogicType {
  any_of = 'any_of',
  all_of = 'all_of'
}

export const LOGIC_TYPE_TO_DISPLAY_NAME_MAPPING: Record<LogicType, string> = {
  [LogicType.any_of]: 'any of',
  [LogicType.all_of]: 'all of',
  [LogicType.none_of]: 'none of'
}

export const ADVANCED_LOGIC_TYPE_TO_DISPLAY_NAME_MAPPING: Record<
  AdvancedLogicType,
  string
> = {
  [AdvancedLogicType.any_of]: 'any of',
  [AdvancedLogicType.all_of]: 'all of'
}

export enum ConditionTypeColor {
  blue = 'blue',
  yellow = 'yellow'
}

/**
 * Condition Type defines under what condition the status option should appear.
 */
export enum ConditionType {
  is_filled = 'is_filled',
  not_filled = 'not_filled',
  is_equal = 'is_equal',
  not_equal = 'not_equal'
}

export enum AdvancedConditionType {
  is_filled = 'is_filled',
  not_filled = 'not_filled',
  is_equal = 'is_equal',
  not_equal = 'not_equal',
  contains = 'contains',
  does_not_contain = 'does_not_contain'
}

export const CONDITION_TYPE_TO_DISPLAY_NAME_MAPPING: Record<
  ConditionType,
  string
> = {
  [ConditionType.is_filled]: 'filled',
  [ConditionType.not_filled]: 'not filled',
  [ConditionType.is_equal]: '',
  [ConditionType.not_equal]: 'not'
}

export const ADVANCED_CONDITION_TYPE_TO_DISPLAY_NAME_MAPPING: Record<
  AdvancedConditionType,
  string
> = {
  [AdvancedConditionType.is_filled]: 'is filled',
  [AdvancedConditionType.not_filled]: 'is not filled',
  [AdvancedConditionType.is_equal]: 'is',
  [AdvancedConditionType.not_equal]: 'is not',
  [AdvancedConditionType.contains]: 'contains',
  [AdvancedConditionType.does_not_contain]: 'does not contain'
}

/**
 * Base interface representing the status option schema in the backend
 */
export interface BaseStatusOption {
  id: string
  index: number
  label: string
  parent_id?: string
  logic_type?: LogicType | AdvancedLogicType
  selected_attributes?: ProductAttributeField[]
  condition_type?: ConditionType | AdvancedConditionType
  text_value?: string
  color: StatusDisplayCellColor
  /**
   * Children of the option.
   */
  children?: StatusOption[] | null
}

/**
 * Extended interface for frontend use, adding UI-specific fields
 */
export interface StatusOption extends BaseStatusOption {
  /**
   * Whether the option is a newly created option that only exists in the FE.
   * Frontend-only attribute, not persisted in the backend
   */
  is_new_field?: boolean
}

export type StatusTrackerStatistics = {
  attribute_field_id: string
  status_tracker_label: string
  status_option_statistics: StatusOptionStatistics[]
}

export interface StatusOptionStatistics {
  num_of_product_items: number
  status_option_label: string
}

// NOTE: There is a UNIQUE_ID_FIELD in the backend as well. Make sure to keep them in sync.
export const UNIQUE_ID_FIELD = 'unique_id'

export const TAXONOMY_ATTRIBUTES = 'taxonomy_attributes'

export const SKU_FIELD = 'sku'
// NOTE: There is a `SYNCED_CATALOGS_FIELD` in the backend as well. Make sure to keep them in sync.
export const SYNCED_CATALOGS_FIELD = 'Catalogs'
export const DEFAULT_IS_LOV_ENABLED_VALUE = false

export const SUPPORTED_PRODUCT_ATTRIBUTE_TYPES_FOR_LOV_SUGGESTIONS = [
  ProductAttributeType.single_tag,
  ProductAttributeType.multi_tags
]

export const DEFAULT_CONFIDENCE_SCORE_THRESHOLD = 0.5
export const CONFIDENCE_SCORE_THRESHOLD_INTERVAL = 0.1
export const CONFIDENCE_SCORE_THRESHOLD_MINIMUM = 0.1
export const CONFIDENCE_SCORE_THRESHOLD_MAXIMUM = 1.0
